import { Dropdown, Field, makeStyles, Option, tokens } from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  BoolEdit,
  ChoiceEdit,
  EditorLabelPosition,
  NumberEdit,
  Section,
  SectionColumn,
  TextEdit,
} from '@powerportalspro/react-fluent';
import { useState } from 'react';

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Layout.Section.SectionLabelPositionDemo';

// `Inherit` leaves the column's position unset, so it follows the section's.
const INHERIT = 'inherit';

const POSITIONS = [EditorLabelPosition.Auto, EditorLabelPosition.Above, EditorLabelPosition.Side] as const;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': { whiteSpace: 'nowrap' },
  },
  selectInput: { minWidth: '125px' },
});

// Localization keys use PascalCase names (`Inherit`, `Auto`, `Above`, `Side`).
const pascal = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

export function SectionLabelPositionSample() {
  const styles = useStyles();
  const t = useT();
  const [section, setSection] = useState<EditorLabelPosition>(EditorLabelPosition.Auto);
  const [column, setColumn] = useState<EditorLabelPosition | undefined>(undefined);

  const positionText = (value: string) => t(`${STRINGS_BASE}.label-positions.${pascal(value)}`);

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Field
              label={t(`${STRINGS_BASE}.section-label-position`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={positionText(section)}
                selectedOptions={[section]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setSection(d.optionValue as EditorLabelPosition);
                }}
              >
                {POSITIONS.map((value) => (
                  <Option key={value} value={value}>
                    {positionText(value)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.column-label-position`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={positionText(column ?? INHERIT)}
                selectedOptions={[column ?? INHERIT]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue)
                    setColumn(
                      d.optionValue === INHERIT ? undefined : (d.optionValue as EditorLabelPosition),
                    );
                }}
              >
                {[INHERIT, ...POSITIONS].map((value) => (
                  <Option key={value} value={value}>
                    {positionText(value)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>
        }
      >
        <Section editorLabelPosition={section}>
          <SectionColumn headerText={t(`${STRINGS_BASE}.column1-header`)}>
            <TextEdit columnName="name" />
            <TextEdit columnName="telephone1" />
            <BoolEdit columnName="creditonhold" />
          </SectionColumn>
          <SectionColumn
            headerText={t(`${STRINGS_BASE}.column2-header`)}
            {...(column !== undefined && { editorLabelPosition: column })}
          >
            <TextEdit columnName="address1_city" />
            <NumberEdit columnName="numberofemployees" />
            <ChoiceEdit columnName="industrycode" />
          </SectionColumn>
        </Section>
      </SectionColumn>
    </Section>
  );
}
